import React, { useContext, useMemo, useState, useRef, useEffect } from "react";
import { dataGridSortNumber } from "../../utils/dataGridSorting.js";
import ArrowUp from "./../../images/remixicons/Arrows/arrow-drop-up-line.js";
import ArrowDown from "./../../images/remixicons/Arrows/arrow-drop-down-line.js";
import ArrowLeft from "./../../images/remixicons/Arrows/arrow-drop-left-line.js";
import ArrowRight from "./../../images/remixicons/Arrows/arrow-drop-right-line.js";
import SvgIcon from "../SvgIcon.jsx";
import { DataGrid2Context } from "./DataGrid2Context.jsx";

// Re-export the provider for compatibility
export { DataGrid2Provider } from "./DataGrid2Context.jsx";

// Truncate text helper
const truncateText = (value, maxLength, showFullText) => {
    const isStringValue = typeof value === "string";
    const needsTruncation = isStringValue && value.length > maxLength;
    const displayValue = isStringValue && !showFullText && needsTruncation
        ? value.slice(0, maxLength) + ".."
        : value;
    return { displayValue, needsTruncation };
};

// Debounce utility
const debounce = (callback, delay = 50) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => callback(...args), delay);
    };
};

// Column width synchronization registry
const groupRegistry = new Map();

function registerTable(group, tableElement, columnsToSync) {
    let registryEntry = groupRegistry.get(group);
    if (!registryEntry) {
        registryEntry = { tables: new Set(), columnsToSync, syncWidths: null };
        const canvas = document.createElement("canvas");
        const canvasContext = canvas.getContext("2d");

        registryEntry.syncWidths = debounce(() => {
            const maxWidths = {};
            registryEntry.columnsToSync.forEach(columnIndex => (maxWidths[columnIndex] = 0));
            registryEntry.tables.forEach((currentTable) => {
                const headerRows = Array.from(currentTable.tHead?.rows || []);
                const bodyRows = Array.from(currentTable.tBodies[0]?.rows || []);
                for (const row of [...headerRows, ...bodyRows]) {
                    for (const columnIndex of registryEntry.columnsToSync) {
                        const cell = row.cells[columnIndex];
                        if (!cell) continue;
                        let contentWidth = 0;
                        if (row.parentElement.tagName === "THEAD") {
                            const divElement = cell.firstElementChild;
                            if (!divElement) continue;
                            const computedStyle = getComputedStyle(divElement);
                            canvasContext.font = [
                                computedStyle.fontStyle, computedStyle.fontVariant,
                                computedStyle.fontWeight, computedStyle.fontSize,
                                computedStyle.fontFamily
                            ].join(" ");
                            const textContent = Array.from(divElement.childNodes)
                                .filter(node => node.nodeType === Node.TEXT_NODE)
                                .map(node => node.textContent.trim())
                                .join(" ");
                            contentWidth = canvasContext.measureText(textContent).width;
                        } else {
                            const innerElement = cell.firstElementChild;
                            if (!innerElement) continue;
                            contentWidth = innerElement.getBoundingClientRect().width;
                        }
                        const cellStyle = getComputedStyle(cell);
                        const padding = parseFloat(cellStyle.paddingLeft) + parseFloat(cellStyle.paddingRight);
                        const totalWidth = contentWidth + padding;
                        if (totalWidth > maxWidths[columnIndex]) maxWidths[columnIndex] = totalWidth;
                    }
                }
            });
            // Apply widths: min-width for first column only, both min and max for others
            registryEntry.tables.forEach((currentTable) => {
                const headerRows = Array.from(currentTable.tHead?.rows || []);
                const bodyRows = Array.from(currentTable.tBodies[0]?.rows || []);
                for (const row of [...headerRows, ...bodyRows]) {
                    for (const columnIndex of registryEntry.columnsToSync) {
                        const cell = row.cells[columnIndex];
                        if (!cell) continue;
                        const width = Math.ceil(maxWidths[columnIndex]);
                        cell.style.minWidth = `${width}px`;
                        if (columnIndex !== 0) {
                            cell.style.maxWidth = `${width}px`;
                        }
                    }
                }
            });
        }, 50);
        groupRegistry.set(group, registryEntry);
    } else {
        registryEntry.columnsToSync = columnsToSync;
    }
    registryEntry.tables.add(tableElement);
    registryEntry.syncWidths();
}

function unregisterTable(group, tableElement) {
    const registryEntry = groupRegistry.get(group);
    if (!registryEntry) return;
    registryEntry.tables.delete(tableElement);
    if (registryEntry.tables.size === 0) groupRegistry.delete(group);
    else registryEntry.syncWidths();
}

// Toggle Button Component
function ToggleFullTextButton({ showFullText, onToggle, needsTruncation }) {
    if (!needsTruncation) {
        return (
            <button className="w-5 h-5 ml-2 opacity-0 pointer-events-none">
                <SvgIcon source={showFullText ? ArrowLeft : ArrowRight} />
            </button>
        );
    }
    return (
        <button
            onClick={onToggle}
            className="w-5 h-5 rounded-full border border-gray-400 ml-2 hover:border-gray-600 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            title={showFullText ? "Hide full text" : "Show full text"}
        >
            <SvgIcon source={showFullText ? ArrowLeft : ArrowRight} />
        </button>
    );
}

export default function DataGrid2({
                                      columns,
                                      dataSource,
                                      showHeader = true,
                                      hasTotal = false,
                                      onRowDoubleClick,
                                      group = "",
                                      syncColumns,
                                      truncateFirstColumn = true
                                  }) {
    const { getShowFullText, toggleShowFullText } = useContext(DataGrid2Context);
    const [localShowFullText, setLocalShowFullText] = useState(false);

    // Determine visibility: use context if grouped and provider is available, otherwise local state
    const showFullText = group && typeof getShowFullText === "function"
        ? getShowFullText(group)
        : localShowFullText;
    const toggleFullText = () => {
        if (group && typeof toggleShowFullText === "function") {
            toggleShowFullText(group);
        } else {
            setLocalShowFullText(prev => !prev);
        }
    };

    // Sorting state
    const [isAscending, setIsAscending] = useState(false);
    const [sortColumnName, setSortColumnName] = useState(null);
    const sortedDataSource = useMemo(() => {
        if (!Array.isArray(dataSource)) return [];
        if (!sortColumnName) return dataSource;
        const column = columns.find(col => col.name === sortColumnName);
        return column?.sort
            ? [...dataSource].sort((a, b) => column.sort(a, b, sortColumnName, isAscending))
            : [...dataSource].sort((a, b) =>
                dataGridSortNumber(a, b, sortColumnName, isAscending)
            );
    }, [dataSource, columns, sortColumnName, isAscending]);
    const lastRowIndex = useMemo(() => (dataSource?.length ?? 0) - 1, [dataSource]);

    const handleSort = columnName => {
        if (sortColumnName !== columnName) {
            setSortColumnName(columnName);
            setIsAscending(false);
        } else if (!isAscending) {
            setIsAscending(true);
        } else {
            setSortColumnName(null);
        }
    };

    // Render individual cell
    const renderCell = (rowData, column, columnIndex) => {
        const rawValue = rowData[column.name] ?? "";
        const isNegative = (typeof rawValue === "number" && rawValue < 0) ||
            (typeof rawValue === "object" && rawValue.amount < 0);
        const renderedValue = column.render ? column.render({ data: rowData }) : rawValue;

        if (columnIndex === 0 && truncateFirstColumn) {
            const maxLength = window.innerWidth < 768 ? 25 : 35;
            const { displayValue, needsTruncation } = truncateText(rawValue, maxLength, showFullText);
            let actualValue = column.render ? column.render({ data: rowData }) : displayValue;
            return (
                <div className="flex items-center">
                    <span
                        className={`truncate inline-block font-bold text-gray-800 dark:text-gray-200 ${
                            !showFullText
                                ? window.innerWidth < 768 ? "w-[205px]" : "w-[275px]"
                                : ""
                        } ${isNegative ? "text-red-700" : ""}`}
                    >
                        {actualValue}
                    </span>
                    <ToggleFullTextButton
                        showFullText={showFullText}
                        onToggle={toggleFullText}
                        needsTruncation={needsTruncation}
                    />
                </div>
            );
        }
        return (
            <span className={`${columnIndex === 0 ? "font-bold text-gray-800 dark:text-gray-200" : ""} ${isNegative ? "text-red-700" : ""}`}>
                {renderedValue}
            </span>
        );
    }

    // Determine which columns to sync (include first when expanded or when truncation is disabled)
    const tableRef = useRef(null);
    const effectiveSyncColumns = useMemo(() => {
        const allColumns = Array.isArray(syncColumns) && syncColumns.length
            ? syncColumns.slice()
            : columns.map((_, index) => index);
        if (!truncateFirstColumn) {
            return allColumns;
        }
        return showFullText ? allColumns : allColumns.filter(index => index !== 0);
    }, [syncColumns, columns, showFullText, truncateFirstColumn]);

    useEffect(() => {
        const tableElement = tableRef.current;
        if (group && tableElement && effectiveSyncColumns.length) {
            registerTable(group, tableElement, effectiveSyncColumns);
            return () => unregisterTable(group, tableElement);
        }
    }, [group, JSON.stringify(effectiveSyncColumns)]);

    // Clear min-width on first column when collapsed (only if truncation is enabled)
    useEffect(() => {
        if (truncateFirstColumn && !showFullText && tableRef.current) {
            const tableElement = tableRef.current;
            const headerRows = Array.from(tableElement.tHead?.rows || []);
            const bodyRows = Array.from(tableElement.tBodies[0]?.rows || []);
            [...headerRows, ...bodyRows].forEach(row => {
                const cell = row.cells[0];
                if (cell) {
                    cell.style.minWidth = '';
                }
            });
        }
    }, [showFullText, truncateFirstColumn]);

    return (
        <div className="w-full overflow-x-auto">
            <table ref={tableRef} className="table-auto w-full dark:text-gray-300">
                {showHeader && (
                    <thead className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
                    <tr>
                        {columns.map((column, columnIndex) => (
                            <th
                                key={columnIndex}
                                className="p-2 first:pl-2.5 last:pr-2.5 md:first:pl-5 md:last:pr-5 whitespace-nowrap"
                            >
                                <div
                                    className={`font-semibold flex items-center ${
                                        column.textAlign === "end" ? "justify-end" : "justify-start"
                                    } ${column.sort ? "cursor-pointer" : ""}`}
                                    onClick={() => column.sort && handleSort(column.name)}
                                >
                                    {column.header}
                                    {sortColumnName === column.name && (
                                        <span className="w-5 -mr-1 -ml-0.5">
                                            <SvgIcon source={isAscending ? ArrowUp : ArrowDown} />
                                        </span>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>
                )}
                <tbody className="text-sm divide-y divide-gray-200 dark:divide-gray-700">
                {sortedDataSource.map((rowData, rowIndex) => (
                    <tr
                        key={rowIndex}
                        className={`
                            ${rowIndex % 2 === 1
                            ? "bg-gray-50 hover:bg-violet-100 dark:bg-gray-900/60 dark:hover:bg-violet-800/15"
                            : "bg-white hover:bg-violet-50 dark:bg-gray-900 hover:dark:bg-violet-800/10"}
                            `}
                        onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(rowData)}
                    >
                        {columns.map((column, columnIndex) => {
                            const cellClasses = [
                                "px-2 py-2 md:py-3 first:pl-2.5 last:pr-2.5 md:first:pl-5 md:last:pr-5 whitespace-nowrap",
                                column.textAlign === "end" ? "text-right" : "",
                                hasTotal && rowIndex === lastRowIndex ? "font-bold border-t" : "",
                                columnIndex === 0 && rowIndex !== lastRowIndex ? "font-medium" : "",
                                columnIndex === 0 && rowIndex === lastRowIndex ? "font-bold border-t" : ""
                            ]
                                .filter(Boolean)
                                .join(" ");
                            return (
                                <td key={columnIndex} className={cellClasses}>
                                    {renderCell(rowData, column, columnIndex)}
                                </td>
                            );
                        })}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}
